import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, MapPin, CreditCard, Wallet, CheckCircle2, ShoppingBag, Bike } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const Checkout = () => {
  const { items, restaurant, subtotal, deliveryFee, total, clearCart } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [payment, setPayment] = useState('card');
  const [placed, setPlaced] = useState(false);
  const [form, setForm] = useState({ name: '', address: 'Via Roma 12, Milano', phone: '', notes: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const placeOrder = (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.phone) {
      toast({ title: 'Campi mancanti', description: 'Compila nome, indirizzo e telefono.', variant: 'destructive' });
      return;
    }
    setPlaced(true);
    clearCart();
  };

  if (placed) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="text-center max-w-md">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent mb-5">
              <CheckCircle2 className="h-11 w-11 text-primary" />
            </div>
            <h1 className="font-display text-3xl font-extrabold text-foreground">Ordine confermato!</h1>
            <p className="mt-3 text-muted-foreground">Grazie {form.name?.split(' ')[0]}! Il tuo ordine è in preparazione e arriverà in circa 30 minuti all'indirizzo {form.address}.</p>
            <div className="mt-6 flex items-center justify-center gap-2 text-primary font-semibold">
              <Bike className="h-5 w-5" /> Il rider è in arrivo
            </div>
            <Button onClick={() => navigate('/')} className="mt-8 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              Torna alla home
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-secondary mb-4">
              <ShoppingBag className="h-9 w-9 text-muted-foreground" />
            </div>
            <p className="font-display font-semibold text-foreground">Il carrello è vuoto</p>
            <Link to="/" className="text-primary font-semibold mt-2 inline-block">Scopri i ristoranti</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ChevronLeft className="h-4 w-4" /> Continua lo shopping
        </button>
        <h1 className="font-display text-3xl font-extrabold text-foreground mb-6">Checkout</h1>

        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          <form onSubmit={placeOrder} className="space-y-6">
            <div className="rounded-2xl border border-border bg-white p-6">
              <h2 className="font-display font-bold text-lg text-foreground flex items-center gap-2 mb-4"><MapPin className="h-5 w-5 text-primary" /> Indirizzo di consegna</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nome e cognome</Label>
                  <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Mario Rossi" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+39 333 1234567" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="address">Indirizzo</Label>
                  <Input id="address" name="address" value={form.address} onChange={handleChange} placeholder="Via, numero civico, città" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="notes">Note per il rider (opzionale)</Label>
                  <Input id="notes" name="notes" value={form.notes} onChange={handleChange} placeholder="Citofono, piano, indicazioni..." />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-white p-6">
              <h2 className="font-display font-bold text-lg text-foreground mb-4">Metodo di pagamento</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => setPayment('card')} className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors ${payment === 'card' ? 'border-primary bg-accent' : 'border-border hover:border-primary/40'}`}>
                  <CreditCard className={`h-5 w-5 ${payment === 'card' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="font-medium text-foreground">Carta di credito</span>
                </button>
                <button type="button" onClick={() => setPayment('cash')} className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors ${payment === 'cash' ? 'border-primary bg-accent' : 'border-border hover:border-primary/40'}`}>
                  <Wallet className={`h-5 w-5 ${payment === 'cash' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="font-medium text-foreground">Contanti alla consegna</span>
                </button>
              </div>
            </div>

            <Button type="submit" size="lg" className="w-full lg:hidden bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              Conferma ordine · {total.toFixed(2)} €
            </Button>
          </form>

          <aside>
            <div className="sticky top-20 rounded-2xl border border-border bg-white p-6">
              <h2 className="font-display font-bold text-lg text-foreground mb-1">Riepilogo</h2>
              {restaurant && <p className="text-sm text-muted-foreground mb-4">da {restaurant.name}</p>}
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between gap-2 text-sm">
                    <span className="text-foreground"><span className="font-semibold text-primary">{item.qty}×</span> {item.name}</span>
                    <span className="text-muted-foreground shrink-0">{(item.price * item.qty).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Subtotale</span><span>{subtotal.toFixed(2)} €</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Consegna</span><span>{deliveryFee === 0 ? 'Gratis' : `${deliveryFee.toFixed(2)} €`}</span></div>
                <div className="flex justify-between font-display font-bold text-lg text-foreground pt-1"><span>Totale</span><span>{total.toFixed(2)} €</span></div>
              </div>
              <Button onClick={placeOrder} size="lg" className="w-full mt-5 hidden lg:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                Conferma ordine
              </Button>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;
